import { ActionReducer, createFeatureSelector, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import { HomepageActions } from '../actions';
import { INavigationNodes } from '../models/settings.model';
import { IContentBannerAttributes } from '../models/cms';
import { environment } from '../../environments/environment';

/**
 * Homepage state's interfaces
 */
export interface IHomepageShopCategoryState {
  shopCategoryTiles: INavigationNodes[],
  shopCategoryOptionsLoading: boolean,
  shopCategoryLoadingError: string,
  isAnyTileInActiveMode: boolean, // used for deciding if shop categories component should be shown
  shopHomepageContentBannerData: IContentBannerAttributes | null,
  shopStoreSelectionContentBannerData: IContentBannerAttributes | null,
  isContentBannerDataLoading: boolean,
  shopContentBannerDataError: string,
}

/**
 * Homepage state's selectors
 */
export const homepageShopCategoryBaseState: MemoizedSelector<object, IHomepageShopCategoryState> = createFeatureSelector<IHomepageShopCategoryState>('homepageShopCategory');

export const selectShopCategoryOptions: MemoizedSelector<object, INavigationNodes[]> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopCategoryTiles,
);

export const selectShopCategoryOptionsLoading: MemoizedSelector<object, boolean> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopCategoryOptionsLoading,
);

export const selectShopCategoryLoadingError: MemoizedSelector<object, string> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopCategoryLoadingError,
);

export const selectShopCategoryIsAnyTileActive: MemoizedSelector<object, boolean> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.isAnyTileInActiveMode,
);

export const selectShopHomepageContentBannerData: MemoizedSelector<object, IContentBannerAttributes | null> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopHomepageContentBannerData,
);

export const selectShopStoreSelectionContentBannerData: MemoizedSelector<object, IContentBannerAttributes | null> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopStoreSelectionContentBannerData,
);

export const selectShopContentBannerDataLoading: MemoizedSelector<object, boolean> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.isContentBannerDataLoading,
);

export const selectShopContentBannerDataError: MemoizedSelector<object, string> = createSelector(
  homepageShopCategoryBaseState,
  (state: IHomepageShopCategoryState) => state.shopContentBannerDataError,
);

/**
 * Homepage initialization states
 */
export const initialShopCategoryState: IHomepageShopCategoryState = {
  shopCategoryTiles: [],
  shopCategoryOptionsLoading: false,
  shopCategoryLoadingError: '',
  isAnyTileInActiveMode: true,
  shopHomepageContentBannerData: null,
  shopStoreSelectionContentBannerData: null,
  isContentBannerDataLoading: false,
  shopContentBannerDataError: '',
};

/**
 * Homepage reducers
 */
export const HomepageShopCategoryReducer: ActionReducer<IHomepageShopCategoryState> = createReducer(
  initialShopCategoryState,

  on(HomepageActions.loadShopCategoryNavigation, (state): IHomepageShopCategoryState => {
    return {
      ...state,
      shopCategoryOptionsLoading: true,
    };
  }),
  on(HomepageActions.loadShopCategoryNavigationSuccess, (state, action): IHomepageShopCategoryState => {
    const hasAnyTileIsActiveState: boolean = action.navigationResponseNodes.some(tile => tile.isActive === true);
    return {
      ...state,
      shopCategoryTiles: action.navigationResponseNodes,
      shopCategoryOptionsLoading: false,
      shopCategoryLoadingError: '',
      isAnyTileInActiveMode: hasAnyTileIsActiveState,
    };
  }),
  on(HomepageActions.loadShopCategoryNavigationFail, (state, action): IHomepageShopCategoryState => {
    return {
      ...state,
      shopCategoryTiles: [],
      shopCategoryOptionsLoading: false,
      shopCategoryLoadingError: action.resError,
    };
  }),
  on(HomepageActions.loadShopContentBanner, (state): IHomepageShopCategoryState => {
    return {
      ...state,
      isContentBannerDataLoading: true,
      shopHomepageContentBannerData: null,
    };
  }),
  on(HomepageActions.loadShopContentBannerSuccess, (state, action): IHomepageShopCategoryState => {
    let homepageContentBanner: IContentBannerAttributes | null = state.shopHomepageContentBannerData;
    let storeSelectionContentBanner: IContentBannerAttributes | null = state.shopStoreSelectionContentBannerData;
    if (action.contentBannerResponse.id === environment.homePageBannerKey) {
      homepageContentBanner = action.contentBannerResponse.attributes;
    }
    if (action.contentBannerResponse.id === environment.storeSelectionBannerKey) {
      storeSelectionContentBanner = action.contentBannerResponse.attributes;
    }
    return {
      ...state,
      isContentBannerDataLoading: false,
      shopHomepageContentBannerData: homepageContentBanner,
      shopStoreSelectionContentBannerData: storeSelectionContentBanner,
      shopContentBannerDataError: '',
    };
  }),
  on(HomepageActions.loadShopContentBannerFail, (state, action): IHomepageShopCategoryState => {
    return {
      ...state,
      isContentBannerDataLoading: false,
      shopHomepageContentBannerData: null,
      shopStoreSelectionContentBannerData: null,
      shopContentBannerDataError: action.resError,
    };
  }),
);
